import { useMemo } from 'react';

import {
   ReferralType,
   VisitDiagnosis,
   VisitHistoryElectronicReferral,
   VisitHistoryElectronicReferralQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Translate } from '@dreryk/react-common';
import {
   AlignItems,
   CheckIcon,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   IconFillColor,
   IconSize,
   IconWrapper,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { DiseaseItemList } from '../appointment/table/cells/DiseaseItemList/DiseaseItemList';
import { visitHistoryTranslationKeys } from '../translations';

import { LocationSpecialityCell } from './cells/LocationSpecialityCell';

const useVisitHistoryElectronicReferralDataConfiguration = (
   visitId: number
): LazyLoadedDataConfigurationByCqrsQuery<VisitHistoryElectronicReferral> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<VisitHistoryElectronicReferral>(() => {
      const query = new VisitHistoryElectronicReferralQuery();
      query.visitId = visitId;
      return query;
   });
};

const useVisitHistoryElectronicReferralColumnsConfiguration =
   // eslint-disable-next-line max-lines-per-function
   (): TableColumnConfiguration<VisitHistoryElectronicReferral>[] => {
      // eslint-disable-next-line max-lines-per-function
      return useMemo<TableColumnConfiguration<VisitHistoryElectronicReferral>[]>(() => {
         const columnsWidth = createDynamicColumnsWidth({
            referralType: '119px',
            locationSpecialtyName: '100%',
            diagnoses: '104px',
            examinationTypes: '87px',
            cito: '52px',
            accessCode: '56px',
         });

         return [
            {
               name: 'referralType',
               label: visitHistoryTranslationKeys.electronicReferralTable.referralType,
               width: columnsWidth.referralType,
               accessor: 'referralType',
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,

               renderer: ({
                  value: referralType,
               }: ValueRendererProps<VisitHistoryElectronicReferral, ReferralType>): JSX.Element => {
                  return (
                     <Translate>
                        {visitHistoryTranslationKeys.electronicReferralTable.referralTypeMap[referralType]}
                     </Translate>
                  );
               },
            },
            {
               name: 'locationSpecialty',
               label: visitHistoryTranslationKeys.electronicReferralTable.locationSpecialtyName,
               width: columnsWidth.locationSpecialtyName,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (referral) => referral,

               renderer: ({
                  value: referral,
               }: ValueRendererProps<VisitHistoryElectronicReferral, VisitHistoryElectronicReferral>): JSX.Element => {
                  return <LocationSpecialityCell referral={referral} />;
               },
            },
            {
               name: 'diagnoses',
               label: visitHistoryTranslationKeys.electronicReferralTable.diagnoses,
               width: columnsWidth.diagnoses,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (referral) => referral.diagnoses,

               renderer: ({
                  value: diagnoses,
               }: ValueRendererProps<VisitHistoryElectronicReferral, VisitDiagnosis[]>): JSX.Element => {
                  return <DiseaseItemList diagnoses={diagnoses} />;
               },
            },
            {
               name: 'examinationTypes',
               label: visitHistoryTranslationKeys.electronicReferralTable.examinationTypes,
               width: columnsWidth.examinationTypes,
               sortingDisabled: true,
               justifyContent: JustifyContent.START,
               alignItems: AlignItems.CENTER,
               accessor: (referral) => referral.procedures,
               formatter: (examinationTypes: string[]) => examinationTypes.join(', '),
            },
            {
               name: 'cito',
               label: visitHistoryTranslationKeys.electronicReferralTable.cito,
               width: columnsWidth.cito,
               sortingDisabled: true,
               justifyContent: JustifyContent.CENTER,
               alignItems: AlignItems.CENTER,
               accessor: (referral) => referral.cito,

               renderer: ({
                  value: cito,
               }: ValueRendererProps<VisitHistoryElectronicReferral, boolean>): JSX.Element | null => {
                  if (!cito) {
                     return null;
                  }

                  return (
                     <IconWrapper size={IconSize.SMALL} testId={'cito'} fillColor={IconFillColor.PRIMARY}>
                        <CheckIcon />
                     </IconWrapper>
                  );
               },
            },
            {
               name: 'accessCode',
               label: visitHistoryTranslationKeys.electronicReferralTable.accessCode,
               width: columnsWidth.accessCode,
               sortingDisabled: true,
               alignItems: AlignItems.CENTER,
               accessor: 'accessCode',
               placeholder: TextPlaceholder.NO_DATA,
            },
         ];
      }, []);
   };

type VisitHistoryElectronicReferralsTableProps = {
   readonly visitId: number;
};

export const VisitHistoryElectronicReferralsTable = (props: VisitHistoryElectronicReferralsTableProps): JSX.Element => {
   return (
      <Table
         testId={'electronicReferrals'}
         dataConfiguration={useVisitHistoryElectronicReferralDataConfiguration(props.visitId)}
         columns={useVisitHistoryElectronicReferralColumnsConfiguration()}
         keyExtractor={'id'}
         pageSize={1000}
         placeholder={
            <EmptyStatePlaceholder
               testId={'noElectronicReferrals'}
               heading={visitHistoryTranslationKeys.electronicReferralTable.noElectronicReferrals}
               subheading={visitHistoryTranslationKeys.electronicReferralTable.noElectronicReferralsWereIssued}
            />
         }
      />
   );
};
